// Gera a rampa de tons (100-900) a partir da cor escolhida pelo escritório em
// Configurações e aplica como CSS custom properties no root — o design system
// (modernist.css) já usa var(--color-accent-*) em todo lugar, então trocar
// essas variáveis retema o sistema inteiro sem tocar em mais nada.
// Mesmo algoritmo do lib/cores.ts do Sistema Fiscal.

function hexParaHsl(hex) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l * 100];

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        default: h = ((r - g) / d + 4) / 6;
    }
    return [h * 360, s * 100, l * 100];
}

function hslParaHex(h, s, l) {
    const sN = s / 100;
    const lN = l / 100;
    const c = (1 - Math.abs(2 * lN - 1)) * sN;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lN - c / 2;
    let [r, g, b] = [0, 0, 0];
    if (h < 60) [r, g, b] = [c, x, 0];
    else if (h < 120) [r, g, b] = [x, c, 0];
    else if (h < 180) [r, g, b] = [0, c, x];
    else if (h < 240) [r, g, b] = [0, x, c];
    else if (h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    const paraHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${paraHex(r)}${paraHex(g)}${paraHex(b)}`;
}

// Luminosidades calibradas para a rampa parecer com a original (100 quase
// branco, 900 quase preto), preservando matiz e saturação da cor escolhida.
const PASSOS = { 100: 92, 200: 83, 300: 70, 400: 58, 500: 46, 600: 38, 700: 30, 800: 23, 900: 16 };

export function gerarRampaCor(hex) {
    const [h, s] = hexParaHsl(hex);
    const saturacaoAjustada = Math.max(s, 45); // evita rampa "lavada" em cores neutras
    const rampa = {};
    for (const [passo, luminosidade] of Object.entries(PASSOS)) {
        rampa[Number(passo)] = hslParaHex(h, saturacaoAjustada, luminosidade);
    }
    return rampa;
}

/**
 * Aplica a cor de destaque do escritório. Sem cor válida, remove os overrides
 * e volta ao dourado padrão do modernist.css.
 *
 * --color-accent (links, kickers, nav ativa) usa um tom mais escuro da rampa
 * no tema claro e mais claro no escuro: a cor cheia costuma ter contraste
 * insuficiente como texto sobre o papel bege.
 */
export function aplicarCorPrimaria(hex) {
    const raiz = document.documentElement.style;
    const sufixos = ['', '-100', '-200', '-300', '-400', '-500', '-600', '-700', '-800', '-900'];

    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
        sufixos.forEach((sufixo) => raiz.removeProperty(`--color-accent${sufixo}`));
        return;
    }

    const rampa = gerarRampaCor(hex);
    for (const [passo, cor] of Object.entries(rampa)) {
        raiz.setProperty(`--color-accent-${passo}`, cor);
    }

    const escuro = document.documentElement.getAttribute('data-theme') === 'dark' ||
        (!document.documentElement.hasAttribute('data-theme') &&
            window.matchMedia('(prefers-color-scheme: dark)').matches);

    raiz.setProperty('--color-accent', escuro ? rampa[300] : rampa[700]);
}
